import React from 'react';

import {
  SearchOutlined,
  QuestionCircleOutlined,
  FileSearchOutlined,
  UserOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Button, Typography, Card } from 'antd';

import { t } from '../../config/translations';

const { Text, Title, Paragraph } = Typography;

// Register this component for debug tracking
debug.registerComponent('OrderNotFound');

type OrderNotFoundProps = {
  clientId?: string;
  orderId?: string;
  onRetry: () => void;
  errorType?: 'client' | 'order' | 'generic';
};

const OrderNotFound = ({
  clientId,
  orderId,
  onRetry,
  errorType = 'generic',
}: OrderNotFoundProps) => {
  debug.trackRender('OrderNotFound');
  debug.log(
    'OrderNotFound',
    `Rendering with errorType: ${errorType}, clientId: ${clientId}, orderId: ${orderId}`,
  );
  const getTitle = () => {
    switch (errorType) {
      case 'client':
        return t('notFound.clientNotFound');
      case 'order':
        return t('notFound.orderNotFound');
      default:
        return t('notFound.noResults');
    }
  };

  const getSubTitle = () => {
    switch (errorType) {
      case 'client':
        return `${t('notFound.noClientFound')} "${clientId}"`;
      case 'order':
        return `${t('notFound.noOrderFound')} "${orderId}" ${t(
          'notFound.forClient',
        )} "${clientId}"`;
      default:
        return t('notFound.noResultsMessage');
    }
  };

  const suggestions = [
    {
      icon: <UserOutlined style={{ color: '#faad14', fontSize: '20px' }} />,
      title: t('notFound.checkClientNumber'),
      description: t('notFound.clientNumberLocation'),
    },
    {
      icon: <ShoppingCartOutlined style={{ color: '#1890ff', fontSize: '20px' }} />,
      title: t('notFound.checkOrderNumber'),
      description: t('notFound.orderNumberLocation'),
    },
    {
      icon: <FileSearchOutlined style={{ color: '#52c41a', fontSize: '20px' }} />,
      title: t('notFound.checkInvoice'),
      description: t('notFound.invoiceInfo'),
    },
  ];

  return (
    <div
      style={{
        maxWidth: '700px',
        margin: '40px auto',
        padding: '0 24px',
        textAlign: 'center',
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 4px 20px rgba(250, 173, 20, 0.2)',
        }}
      >
        <SearchOutlined style={{ fontSize: '42px', color: '#faad14' }} />
      </div>

      {/* Title */}
      <Title level={3} style={{ color: '#d48806', marginBottom: '8px' }}>
        {getTitle()}
      </Title>

      {/* Subtitle */}
      <Paragraph style={{ fontSize: '16px', color: '#595959', marginBottom: '24px' }}>
        {getSubTitle()}
      </Paragraph>

      {/* Info searched */}
      {(clientId || orderId) && (
        <Card
          size="small"
          style={{
            background: '#fafafa',
            borderColor: '#d9d9d9',
            maxWidth: '350px',
            margin: '0 auto 24px',
            textAlign: 'left',
          }}
        >
          <Text
            type="secondary"
            style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}
          >
            {t('notFound.searchedInfo')}
          </Text>
          {clientId && (
            <div style={{ marginBottom: '4px' }}>
              <Text strong>{t('notFound.client')} </Text>
              <Text code>{clientId}</Text>
            </div>
          )}
          {orderId && (
            <div>
              <Text strong>{t('notFound.order')} </Text>
              <Text code>{orderId}</Text>
            </div>
          )}
        </Card>
      )}

      {/* CTA Button */}
      <Button
        type="primary"
        size="large"
        icon={<SearchOutlined />}
        onClick={onRetry}
        style={{
          minWidth: '250px',
          height: '48px',
          fontSize: '16px',
          fontWeight: 600,
          borderRadius: '8px',
          marginBottom: '32px',
        }}
      >
        {t('notFound.newSearch')}
      </Button>

      {/* Suggestions card */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
            <Text strong>{t('notFound.suggestions')}</Text>
          </div>
        }
        style={{
          borderColor: '#91d5ff',
          background: '#f6ffed',
          textAlign: 'left',
        }}
        headStyle={{
          background: 'primary',
          borderBottom: '1px solid #91d5ff',
        }}
      >
        {suggestions.map((item, idx) => (
          <div
            key={item.title}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 0',
              borderBottom: idx < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </div>
            <div>
              <Text
                strong
                style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}
              >
                {item.title}
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                {item.description}
              </Text>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};

export default OrderNotFound;

// Mark this component as initialized
debug.markInitialized('OrderNotFound');
